import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { BUTTON_LABELS, PERMISSIONS } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { AreaDesignation } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { VerticalTabsLayout } from "@mibuildweb/components";
import { useCategories, usePermissions } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { ConfigPageLayout } from "../../../../Config/ConfigPageLayout";
import { IAreaTypeData } from "../AreaTypes.types";
import { addAreaTypeSchema } from "../schema/addAreaType.schema";
import { AreaTypeDetailsTab } from "../tabs/AreaTypeDetailsTab/AreaTypeDetailsTab";
import { AreaTypePreviewTab } from "../tabs/AreaTypePreviewTab/AreaTypePreviewTab";
import { useAddAreaTypeViewModel } from "./useAddAreaTypeViewModel";

const INITIAL_VALUE: IAreaTypeData = {
  name: "",
  areaDesignation: AreaDesignation.General,
  specColumn: 0,
  categories: [],
};

export function AddAreaType() {
  const { params, goBack } = useRouter();
  const { id: editId } = params;
  const theme = useTheme();

  const form = useForm<IAreaTypeData>({
    mode: "onChange",
    defaultValues: INITIAL_VALUE,
    resolver: zodResolver(addAreaTypeSchema),
  });

  const {
    formState: { isValid, isDirty },
    reset,
    getValues,
    watch,
  } = form;

  const { name } = watch();

  const {
    getCategories,
    categories,
    isLoading: isCategoriesLoading,
  } = useCategories();

  const {
    isLoading: isViewModelLoading,
    fetchAreaTypeDetails,
    handleCreateAreaType,
    handleUpdateAreaType,
    areaTypeDetails,
  } = useAddAreaTypeViewModel({ goBack, reset });

  useEffect(() => {
    if (editId) {
      fetchAreaTypeDetails(editId);
    }
    getCategories();
  }, []);

  const isLoading = isCategoriesLoading || isViewModelLoading;

  const { hasPermission } = usePermissions();

  const isReadOnly = !hasPermission(PERMISSIONS.PLAN.UPDATE);

  const tabs = useMemo(
    () => [
      {
        label: "Details",
        value: "details",
        controlledFormViewComponent: AreaTypeDetailsTab,
        extraTabProps: {
          isReadOnly,
          categories,
        },
        isComplete: isValid,
        hasErrors: !isValid,
      },
      {
        label: "Preview",
        value: "preview",
        extraTabProps: {
          originalAreaTypeDetails: areaTypeDetails,
        },
        controlledFormViewComponent: AreaTypePreviewTab,
      },
    ],
    [isValid, categories, isReadOnly, areaTypeDetails]
  );

  const handleSubmit = () => {
    const data = getValues();
    if (editId) {
      handleUpdateAreaType(data);
    } else {
      handleCreateAreaType(data);
    }
  };

  const renderTitle = () => {
    const action = editId ? "Edit" : "Add";
    return (
      <>
        {action} Area:{" "}
        <span style={{ color: theme.palette.primary.main }}>{name}</span>
      </>
    );
  };

  const actions = {
    cancel: {
      label: BUTTON_LABELS.CANCEL,
      onClick: () => {
        goBack();
      },
    },
    submit: {
      label: editId ? "Update Area" : "Save Area",
      onClick: () => {
        handleSubmit();
      },
      disabled: !isDirty || !isValid || isLoading,
    },
  };

  return (
    <ConfigPageLayout
      renderTitle={renderTitle}
      bottomActions={actions}
      loading={isLoading}
    >
      <VerticalTabsLayout tabs={tabs} form={form} transparentBackground />
    </ConfigPageLayout>
  );
}
